import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useWallet } from '@/hooks/useWallet'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_BODY, FONT_HEADING } from '@/config/theme'
import { apiKeyHeader } from '@/services/api'
import {
  getOrCreateSessionId,
  hasAcceptedCurrent,
  hasAcceptedDevice,
  markAcceptedCurrent,
  markAcceptedDevice,
  postAcceptance,
} from '@/utils/termsAcceptance'

// Same-origin by default (matches useFeeQuote/useBurn). VITE_API_URL, if set, is the BARE FE origin.
const API_BASE = import.meta.env.VITE_API_URL || window.location.origin

/**
 * App-level clickwrap gate. Mounted ABOVE <Routes>; when the current-version acceptance is absent
 * for the active wallet (or, pre-connect, the session), it renders a full-screen blocking overlay
 * that intercepts ALL bridge/swap interaction (quote AND transaction) until the user ticks the
 * acceptance box and confirms.
 *
 * Option B (#82): acceptance is prompted ONCE per device per version. Once accepted on this device
 * we suppress the gate for every wallet (device flag), but we still record a per-wallet server
 * evidence row — silently, in the background — for each wallet connected after that first accept.
 *
 * The SERVER row is authoritative: on confirm we POST /api/terms/accept and only persist the
 * localStorage suppressor AFTER a 2xx. A failed/offline POST keeps the overlay up (retry) so no
 * one is gated through without a legal-evidence row.
 */
export function TermsGate() {
  const c = useThemeColors()
  const location = useLocation()
  const { evmAddress, isEvmConnected, solanaAddress, isSolanaConnected } = useWallet()

  // Stable per-device session id (durable key for pre-connect acceptance).
  const sessionId = useMemo(() => getOrCreateSessionId(), [])

  // Identity the acceptance is keyed to: connected wallet (EVM lowercased, else Solana) else session.
  const connectedWallet = useMemo<string | null>(() => {
    if (isEvmConnected && evmAddress) return evmAddress.toLowerCase()
    if (isSolanaConnected && solanaAddress) return solanaAddress
    return null
  }, [isEvmConnected, evmAddress, isSolanaConnected, solanaAddress])

  const walletOrSession = connectedWallet ?? sessionId

  // Device-accepted (any wallet, current version) suppresses the gate too — see Option B (#82).
  const [accepted, setAccepted] = useState<boolean>(
    () => hasAcceptedCurrent(walletOrSession) || hasAcceptedDevice(),
  )
  const [checked, setChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Wallets we have already attempted a background evidence POST for (dedupe per address).
  const bgAttempted = useRef<Set<string>>(new Set())

  // Re-evaluate when the identity changes (connect/disconnect/switch) or version tag bumps.
  useEffect(() => {
    setAccepted(hasAcceptedCurrent(walletOrSession) || hasAcceptedDevice())
    setError(null)
  }, [walletOrSession])

  // Silent per-wallet evidence: once the device has accepted, any newly-connected wallet without its
  // own current-version row gets a BACKGROUND POST recorded (no gate, no UI block). Failures are
  // swallowed — the device flag already suppresses the gate, and the next connect retries.
  useEffect(() => {
    if (!connectedWallet) return
    if (!hasAcceptedDevice()) return
    if (hasAcceptedCurrent(connectedWallet)) return
    if (bgAttempted.current.has(connectedWallet)) return
    bgAttempted.current.add(connectedWallet)
    const wallet = connectedWallet
    void (async () => {
      const ok = await postAcceptance(API_BASE, sessionId, wallet, apiKeyHeader)
      if (ok) {
        // Persist the per-wallet suppressor ONLY after the server row is recorded.
        markAcceptedCurrent(wallet)
      } else {
        // Allow a retry on the next connect of this wallet.
        bgAttempted.current.delete(wallet)
      }
    })()
  }, [connectedWallet, sessionId])

  const onConfirm = useCallback(async () => {
    if (!checked || submitting) return
    setSubmitting(true)
    setError(null)
    const ok = await postAcceptance(API_BASE, sessionId, connectedWallet, apiKeyHeader)
    if (ok) {
      // Persist the suppressors ONLY after the server row is recorded.
      markAcceptedCurrent(walletOrSession)
      markAcceptedDevice()
      setAccepted(true)
    } else {
      setError('Could not record your acceptance. Please check your connection and try again.')
    }
    setSubmitting(false)
  }, [checked, submitting, sessionId, connectedWallet, walletOrSession])

  // The legal pages themselves must never be gated — the user has to be able to READ /terms and
  // /privacy in order to accept them (the checkbox links open those routes in a new tab).
  const onLegalPage = location.pathname === '/terms' || location.pathname === '/privacy'

  if (accepted || onLegalPage) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-gate-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483000,
        background: c.mo || 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, fontFamily: FONT_BODY,
        backdropFilter: 'blur(3px)',
      }}
    >
      <div style={{
        width: '100%', maxWidth: 460, background: c.card,
        border: `1px solid ${c.cb}`, borderRadius: 16,
        padding: '26px 24px 22px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
      }}>
        <div id="terms-gate-title" style={{
          fontFamily: FONT_HEADING, fontSize: 19, fontWeight: 700,
          color: c.tx, letterSpacing: -0.2, marginBottom: 8,
        }}>
          Before you continue
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.6, color: c.mu, marginBottom: 18 }}>
          To use Parse DeFi you must review and accept our terms.
        </div>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
          fontSize: 13.5, lineHeight: 1.55, color: c.tx, marginBottom: 18,
        }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0, cursor: 'pointer', accentColor: c.ac }}
          />
          <span>
            I have read and agree to the{' '}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: c.ac, textDecoration: 'underline' }}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: c.ac, textDecoration: 'underline' }}>
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        {error && (
          <div role="alert" style={{
            fontSize: 12.5, lineHeight: 1.5, color: '#E5484D',
            background: 'rgba(229,72,77,0.10)', border: '1px solid rgba(229,72,77,0.30)',
            borderRadius: 10, padding: '9px 11px', marginBottom: 14,
          }}>
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={onConfirm}
          disabled={!checked || submitting}
          style={{
            width: '100%', padding: '12px 16px', borderRadius: 12, border: 'none',
            fontFamily: FONT_HEADING, fontSize: 15, fontWeight: 650,
            cursor: !checked || submitting ? 'not-allowed' : 'pointer',
            background: !checked || submitting ? c.dv : c.ac,
            color: !checked || submitting ? c.mu : '#FFFFFF',
            transition: 'all 0.15s',
          }}
        >
          {submitting ? 'Recording…' : error ? 'Retry' : 'Accept and continue'}
        </button>
      </div>
    </div>
  )
}
